import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { PaymentState, Charge, ChargePayment } from '../types';

interface PaymentStore extends PaymentState {
  availableCharges: Charge[];
  selectedCharge: Charge | null;
  paymentRequestId: string | null;
  razorpayOrderId: string | null;
  setAvailableCharges: (charges: Charge[]) => void;
  setSelectedCharge: (charge: Charge | null) => void;
  setPaymentRequestId: (id: string | null) => void;
  setRazorpayOrderId: (id: string | null) => void;
  addChargePayment: (chargeId: string, paymentType: 'FULL' | 'PARTIAL', amount?: number) => void;
  updateChargePaymentAmount: (chargeId: string, amount: number) => void;
  removeChargePayment: (chargeId: string) => void;
  calculateTotalAmount: () => void;
  setProcessing: (processing: boolean) => void;
  setCurrentStep: (step: 'selection' | 'payment' | 'success' | 'failure') => void;
  resetPayment: () => void;
  getChargePayment: (chargeId: string) => ChargePayment | undefined;
}

export const usePaymentStore = create<PaymentStore>()(
  subscribeWithSelector((set, get) => ({
    selectedChargePayments: [],
    totalAmount: 0,
    isProcessing: false,
    currentStep: 'selection',
    availableCharges: [],
    selectedCharge: null,
    paymentRequestId: null,
    razorpayOrderId: null,

    setAvailableCharges: (charges: Charge[]) => {
      console.log('Store: setAvailableCharges called with', charges.length, 'charges');
      console.log('Store: first charge example:', charges[0]);
      set({ availableCharges: charges });
    },

    setSelectedCharge: (charge: Charge | null) => {
      console.log('Store: setSelectedCharge called with', charge);
      set({ selectedCharge: charge });
    },

    setPaymentRequestId: (id: string | null) => {
      console.log('Store: setPaymentRequestId called with', id);
      set({ paymentRequestId: id });
    },

    setRazorpayOrderId: (id: string | null) => {
      console.log('Store: setRazorpayOrderId called with', id);
      set({ razorpayOrderId: id });
    },

    addChargePayment: (chargeId: string, paymentType: 'FULL' | 'PARTIAL', amount?: number) => {
      console.log('Store: addChargePayment called', { chargeId, paymentType, amount });
      const state = get();
      console.log('Store: current state', { availableCharges: state.availableCharges.length, selectedChargePayments: state.selectedChargePayments });
      
      const charge = state.availableCharges.find(c => c.id === chargeId);
      console.log('Store: found charge', charge);
      
      if (!charge) {
        console.error('Store: charge not found', chargeId);
        return;
      }

      const existingPaymentIndex = state.selectedChargePayments.findIndex(cp => cp.chargeId === chargeId);
      console.log('Store: existing payment index', existingPaymentIndex);
      
      const newPayment: ChargePayment = {
        chargeId,
        paymentType,
        amount: amount || (paymentType === 'FULL' ? charge.outstandingAmount : 0),
        maxAmount: charge.outstandingAmount
      };
      
      console.log('Store: new payment', newPayment);

      let newSelectedChargePayments: ChargePayment[];
      if (existingPaymentIndex >= 0) {
        // Update existing payment
        newSelectedChargePayments = [...state.selectedChargePayments];
        newSelectedChargePayments[existingPaymentIndex] = newPayment;
        console.log('Store: updated existing payment');
      } else {
        // Add new payment
        newSelectedChargePayments = [...state.selectedChargePayments, newPayment];
        console.log('Store: added new payment');
      }

      console.log('Store: setting new selectedChargePayments', newSelectedChargePayments);
      set({ selectedChargePayments: newSelectedChargePayments });
      get().calculateTotalAmount();
    },

    updateChargePaymentAmount: (chargeId: string, amount: number) => {
      const state = get();
      const paymentIndex = state.selectedChargePayments.findIndex(cp => cp.chargeId === chargeId);
      
      if (paymentIndex >= 0) {
        const newSelectedChargePayments = [...state.selectedChargePayments];
        const payment = { ...newSelectedChargePayments[paymentIndex] };
        
        // Ensure amount doesn't exceed max
        payment.amount = Math.min(amount, payment.maxAmount);
        payment.paymentType = payment.amount >= payment.maxAmount ? 'FULL' : 'PARTIAL';
        
        newSelectedChargePayments[paymentIndex] = payment;
        set({ selectedChargePayments: newSelectedChargePayments });
        get().calculateTotalAmount();
      }
    },

    removeChargePayment: (chargeId: string) => {
      const state = get();
      const newSelectedChargePayments = state.selectedChargePayments.filter(cp => cp.chargeId !== chargeId);
      set({ selectedChargePayments: newSelectedChargePayments });
      get().calculateTotalAmount();
    },

    calculateTotalAmount: () => {
      const state = get();
      const totalAmount = state.selectedChargePayments.reduce((sum, payment) => sum + payment.amount, 0);
      set({ totalAmount });
    },

    getChargePayment: (chargeId: string) => {
      const state = get();
      return state.selectedChargePayments.find(cp => cp.chargeId === chargeId);
    },

    setProcessing: (processing: boolean) => {
      set({ isProcessing: processing });
    },

    setCurrentStep: (step: 'selection' | 'payment' | 'success' | 'failure') => {
      set({ currentStep: step });
    },

    resetPayment: () => {
      set({
        selectedChargePayments: [],
        totalAmount: 0,
        isProcessing: false,
        currentStep: 'selection',
        paymentRequestId: null,
        razorpayOrderId: null
      });
    }
  }))
);